import { useIntl } from 'react-intl';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { EsignatureType } from '~/enums/EsignatureType';
import { api } from '~/redux/api/createApi';

export interface ConfirmDeleteModalProps {
  isVisible: boolean;
  providerName: EsignatureType;
  integrationId: string;
  onHide?: () => void;
}

export const ConfirmDeleteModal = ({
  isVisible,
  providerName,
  integrationId,
  onHide = () => {},
}: ConfirmDeleteModalProps) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { toast: errorToast } = useErrorToast();
  const [deleteIntegration, { isLoading }] =
    api.useDeleteEsignatureIntegrationMutation();

  const handleConfirm = async () => {
    try {
      await deleteIntegration({
        integrationId,
      }).unwrap();

      toast({
        message: intl.formatMessage(
          {
            id: 'NewESignature.ESignatureIntegrationCard.DeleteSuccessMessage',
            defaultMessage: '{providerName} integration was deleted.',
            description:
              'Success message shown after deleting an e-signature integration',
          },
          { providerName },
        ),
        status: 'success',
        autoDismiss: true,
      });

      onHide();
    } catch (error) {
      errorToast(error instanceof Error ? error : new Error(String(error)));
    }
  };

  return (
    <Modal
      enableHideOnPrimaryAction={true}
      isVisible={isVisible}
      title={intl.formatMessage(
        {
          id: 'NewESignature.ESignatureIntegrationCard.ConfirmDeleteTitle',
          defaultMessage: 'Delete {providerName} integration',
          description:
            'Title for integration deletion confirmation modal. Example: providerName = "DocuSign"',
        },
        { providerName },
      )}
      primaryAction={{
        variant: 'danger',
        text: intl.formatMessage({
          id: 'NewESignature.ESignatureIntegrationCard.Delete',
          defaultMessage: 'Delete',
          description: 'Delete button text',
        }),
        onClick: handleConfirm,
        isLoading,
      }}
      onHide={onHide}
    >
      {intl.formatMessage(
        {
          id: 'NewESignature.ESignatureIntegrationCard.ConfirmDeleteMessage',
          defaultMessage:
            'Authenticated users will lose access to the {providerName} integration. Documents that have not been fully signed will need to restart the signature process.',
          description:
            'Message for integration deletion confirmation. Example: providerName = "DocuSign"',
        },
        { providerName },
      )}
    </Modal>
  );
};
